"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  type Option,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type BaseEncoding,
  type BaseError,
  DEFAULT_ENCODE_OPTIONS,
  decodeText,
  type EncodeOptions,
  encodeBytes,
  workingRows,
} from "@/lib/tool-kit/ciphers-misc/base-encodings";
import {
  bytesToHex,
  bytesToText,
  parseHexBytes,
  textToBytes,
} from "@/lib/tool-kit/ciphers-misc/xor";

type DataType = "text" | "hex";

const ROWS_SHOWN = 16;
const ENCODINGS: readonly BaseEncoding[] = [
  "base32",
  "base32hex",
  "base58",
  "ascii85",
  "z85",
];

/** English strings. A translated content file overrides these through `ui`. */
export const BASE_ENCODING_CONVERTER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  sample: "Hello, World!",
  sampleHex: "86 4f d2 6f b5 59 f7 5b",
  inputPlaceholder: "Type or paste here",
  textLabel: "Text (read as UTF-8)",
  hexLabel: "Bytes in hex",
  encodedLabel: "Encoded text",
  decodedText: "Decoded text",
  decodedHex: "Decoded bytes (hex)",
  encoding: "Encoding",
  base32: "Base32 (RFC 4648)",
  base32hex: "Base32 extended hex (RFC 4648)",
  base58: "Base58 (Bitcoin alphabet)",
  ascii85: "Ascii85 (Adobe)",
  z85: "Z85 (ZeroMQ)",
  dataType: "The data is",
  dataText: "Text",
  dataHex: "Hex bytes",
  aboutBase32:
    "Letters A–Z and digits 2–7, five bits per character, padded with = to a multiple of eight characters. Case does not matter when decoding.",
  aboutBase32hex:
    "Digits 0–9 and letters A–V, five bits per character, so the encoded text sorts in the same order as the bytes.",
  aboutBase58:
    "Bitcoin's alphabet leaves out 0, O, I and l. The bytes are read as one big number, and each leading zero byte becomes a 1.",
  aboutAscii85:
    "Four bytes become five printable characters from ! to u. Four zero bytes can be written as z, and the result is usually wrapped in <~ and ~>.",
  aboutZ85:
    "ZeroMQ's variant of base 85, safe to put in source code. The data must be a whole number of 4-byte groups, and there is no padding or shortcut.",
  padding: "Pad with = to a multiple of 8",
  wrap: "Wrap in <~ and ~>",
  zero: "Write four zero bytes as z",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  errBadHex: "The bytes are not valid hex. Use pairs of 0–9 and a–f.",
  errCharacters: "These characters do not belong to this encoding: {items}",
  errLength: "The length is not one this encoding can produce.",
  errOverflow:
    "A group of characters stands for a number too large for four bytes.",
  errZ85:
    "one: Z85 needs a multiple of four bytes: add {count} more byte.|other: Z85 needs a multiple of four bytes: add {count} more bytes.",
  notText:
    "These bytes are not valid UTF-8 text, so some characters show as �. Switch the data to hex bytes to see them exactly.",
  working: "How it was worked out",
  introBase32:
    "The bytes are written as bits and cut into groups of five. Each group is a number from 0 to 31 and picks one character.",
  introBase58:
    "The bytes are read as one big number and written in base 58. These are its digits, most significant first, with the character each one picks.",
  introBase85:
    "Every group of four bytes is read as a 32-bit number and written as five base-85 digits, each picking one character.",
  zeroBytes:
    "one: {count} leading zero byte is written as the character 1.|other: {count} leading zero bytes are each written as the character 1.",
  rowsShown:
    "one: Showing the first row.|other: Showing the first {count} rows.",
  colBits: "Bits",
  colBytes: "Bytes (hex)",
  colDigit: "Digit",
  colValue: "Value",
  colNumber: "Number",
  colCharacter: "Character",
  colCharacters: "Characters",
  emptyWorking: "Encode something to see the working.",
} as const;

type Strings = typeof BASE_ENCODING_CONVERTER_STRINGS;

function encodingOptions(strings: Strings): Option<BaseEncoding>[] {
  return ENCODINGS.map((value) => ({ value, label: strings[value] }));
}

function aboutText(strings: Strings, encoding: BaseEncoding): string {
  const about: Record<BaseEncoding, string> = {
    base32: strings.aboutBase32,
    base32hex: strings.aboutBase32hex,
    base58: strings.aboutBase58,
    ascii85: strings.aboutAscii85,
    z85: strings.aboutZ85,
  };
  return about[encoding];
}

function errorText(
  error: BaseError,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): string {
  switch (error.kind) {
    case "characters":
      return t(strings.errCharacters, { items: error.invalid.join(" ") });
    case "length":
      return strings.errLength;
    case "overflow":
      return strings.errOverflow;
    default:
      return tPlural(strings.errZ85, locale, error.missing, {
        count: fmt(error.missing),
      });
  }
}

interface Converted {
  bytes: Uint8Array | null;
  output: string;
  error: string | null;
  notText: boolean;
}

function convertEncode(
  input: string,
  encoding: BaseEncoding,
  dataType: DataType,
  options: EncodeOptions,
  describe: (error: BaseError) => string,
  strings: Strings
): Converted {
  const bytes = dataType === "hex" ? parseHexBytes(input) : textToBytes(input);
  if (bytes === null) {
    return { bytes, output: "", error: strings.errBadHex, notText: false };
  }
  const result = encodeBytes(encoding, bytes, options);
  if (!result.ok) {
    return { bytes, output: "", error: describe(result.error), notText: false };
  }
  return { bytes, output: result.text, error: null, notText: false };
}

function convertDecode(
  input: string,
  encoding: BaseEncoding,
  dataType: DataType,
  describe: (error: BaseError) => string
): Converted {
  const result = decodeText(encoding, input);
  if (!result.ok) {
    return {
      bytes: null,
      output: "",
      error: describe(result.error),
      notText: false,
    };
  }
  if (dataType === "hex") {
    return {
      bytes: result.bytes,
      output: bytesToHex(result.bytes),
      error: null,
      notText: false,
    };
  }
  const { text, valid } = bytesToText(result.bytes);
  return { bytes: result.bytes, output: text, error: null, notText: !valid };
}

function Working({
  strings,
  encoding,
  bytes,
}: {
  strings: Strings;
  encoding: BaseEncoding;
  bytes: Uint8Array | null;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (bytes === null || bytes.length === 0) {
    return <Notice>{strings.emptyWorking}</Notice>;
  }
  const rows = workingRows(encoding, bytes, ROWS_SHOWN + 1);
  const shown = rows.slice(0, ROWS_SHOWN);
  const leadingZeros = bytes.findIndex((byte) => byte !== 0);
  const zeros = leadingZeros === -1 ? bytes.length : leadingZeros;
  const heads: Record<string, string[]> = {
    base32: [strings.colBits, strings.colValue, strings.colCharacter],
    base58: [strings.colDigit, strings.colValue, strings.colCharacter],
    default: [strings.colBytes, strings.colNumber, strings.colCharacters],
  };
  const intros: Record<string, string> = {
    base32: strings.introBase32,
    base58: strings.introBase58,
    default: strings.introBase85,
  };
  let key = "default";
  if (encoding === "base32" || encoding === "base32hex") {
    key = "base32";
  } else if (encoding === "base58") {
    key = "base58";
  }
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm leading-relaxed">
        {intros[key]}
      </p>
      <DataTable
        head={heads[key]}
        rows={shown.map((row, index) => [
          key === "base58" ? fmt(index + 1) : row.input,
          fmt(row.value),
          row.output,
        ])}
      />
      {rows.length > ROWS_SHOWN ? (
        <p className={HINT}>
          {tPlural(strings.rowsShown, locale, ROWS_SHOWN, {
            count: fmt(ROWS_SHOWN),
          })}
        </p>
      ) : null}
      {key === "base58" && zeros > 0 ? (
        <p className={HINT}>
          {tPlural(strings.zeroBytes, locale, zeros, { count: fmt(zeros) })}
        </p>
      ) : null}
    </div>
  );
}

export default function BaseEncodingConverterWidget() {
  const strings = useToolUi(BASE_ENCODING_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [encoding, setEncoding] = useState<BaseEncoding>("base32");
  const [dataType, setDataType] = useState<DataType>("text");
  const [options, setOptions] = useState(DEFAULT_ENCODE_OPTIONS);

  const encoding32 = encoding === "base32" || encoding === "base32hex";
  const encoding85 = encoding === "ascii85";
  const describe = (error: BaseError) => errorText(error, strings, locale, fmt);
  const converted =
    mode === "encode"
      ? convertEncode(input, encoding, dataType, options, describe, strings)
      : convertDecode(input, encoding, dataType, describe);
  const encodingMode = mode === "encode";

  function changeDataType(next: DataType) {
    if (
      encodingMode &&
      (input === strings.sample || input === strings.sampleHex)
    ) {
      setInput(next === "hex" ? strings.sampleHex : strings.sample);
    }
    setDataType(next);
  }

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={aboutText(strings, encoding)}
          id="be-encoding"
          label={strings.encoding}
          onChange={setEncoding}
          options={encodingOptions(strings)}
          value={encoding}
        />
        <SelectField
          id="be-data"
          label={strings.dataType}
          onChange={changeDataType}
          options={[
            { value: "text", label: strings.dataText },
            { value: "hex", label: strings.dataHex },
          ]}
          value={dataType}
        />
      </div>
      {encodingMode && encoding32 ? (
        <Checkbox
          checked={options.padding}
          label={strings.padding}
          onChange={(padding) => setOptions({ ...options, padding })}
        />
      ) : null}
      {encodingMode && encoding85 ? (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={options.wrap}
            label={strings.wrap}
            onChange={(wrap) => setOptions({ ...options, wrap })}
          />
          <Checkbox
            checked={options.zeroShortcut}
            label={strings.zero}
            onChange={(zeroShortcut) =>
              setOptions({ ...options, zeroShortcut })
            }
          />
        </div>
      ) : null}
    </div>
  );

  const notices = (
    <div className="space-y-2">
      {converted.error ? <ErrorNotice>{converted.error}</ErrorNotice> : null}
      {converted.notText ? <Notice>{strings.notText}</Notice> : null}
    </div>
  );

  let inputLabel: string = strings.encodedLabel;
  let outputLabel: string = strings.encodedLabel;
  if (encodingMode) {
    inputLabel = dataType === "text" ? strings.textLabel : strings.hexLabel;
  } else {
    outputLabel = dataType === "hex" ? strings.decodedHex : strings.decodedText;
  }

  return (
    <CipherWorkbench
      idPrefix="be"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: inputLabel,
        inputPlaceholder: strings.inputPlaceholder,
        output: outputLabel,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={converted.output}
      working={
        <Working
          bytes={converted.bytes}
          encoding={encoding}
          strings={strings}
        />
      }
    />
  );
}
